import { useCallback, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { SELLER_BRAND_TYPE } from 'const/brands'
import { Campaign } from 'types'

const MIN_ASIN_LENGTH = 10

function scheduleMicrotask(callback: () => void): void {
    if (typeof queueMicrotask === 'function') {
        queueMicrotask(callback)
        return
    }
    void Promise.resolve().then(callback)
}

export const useValidator = (
    campaign: Campaign | null
): [(incomingAsins: string[]) => boolean, string] => {
    const { t } = useTranslation('campaigns')
    const [errorMsg, setErrorMsg] = useState('')
    const isSellerBrandType = campaign?.profile.type === SELLER_BRAND_TYPE
    const identifierType = isSellerBrandType
        ? t('campaigns:createProductAd.skuLabel', 'SKU')
        : t('campaigns:createProductAd.asinLabel', 'ASIN')

    const validator = useCallback(
        (incomingAsins: string[]): boolean => {
            const identifiers = incomingAsins
                .map((item) => item.trim())
                .filter((item) => item.length > 0)

            const hasDuplicates = identifiers.some(
                (item, index) => identifiers.indexOf(item) !== index
            )

            let nextError = ''
            if (hasDuplicates) {
                nextError = t(
                    'campaigns:createProductAd.duplicateInList',
                    'A {{type}} you are trying to add has already been entered',
                    { type: identifierType }
                )
            } else if (
                !isSellerBrandType &&
                identifiers.some((item) => item.length < MIN_ASIN_LENGTH)
            ) {
                nextError = t(
                    'campaigns:createProductAd.asinTooShort',
                    'ASIN must be at least {{min}} characters long',
                    { min: MIN_ASIN_LENGTH }
                )
            }

            // BulkAddTextArea calls this during its own setState.
            scheduleMicrotask(() => {
                setErrorMsg(nextError)
            })
            return nextError === ''
        },
        [identifierType, isSellerBrandType, t]
    )

    return [validator, errorMsg]
}
